<template>
  <div class="hypo-allocation-table">
    <mds-data-table size="medium" :header-configs="headers" :row-data="rows">
      <template v-slot:header>
        <thead class="table-header-names">
          <tr>
            <th class="table-color-column"></th>
            <th
              v-for="column in componentData.columns"
              :key="column.value"
              class="table-header-name-column"
              :style="{ width: getColWidth(column.value) }"
            >
              <div
                :id="`${column.value}-aa`"
                @mouseover="e => showName(`${column.value}-aa`)"
                @mouseleave="e => hideName()"
              >
                {{ column.text }}
              </div>
              <mds-tooltip
                v-if="currentTrigger === `${column.value}-aa`"
                v-model="toggle"
                class="allocation-tooltip"
                :triggered-by="currentTrigger"
                :position="['bottom-center']"
              >
                {{ column.text }}
              </mds-tooltip>
            </th>
          </tr>
        </thead>
        <thead class="table-header-total">
          <tr>
            <th class="table-color-column"></th>
            <th
              v-for="column in componentData.columns"
              :key="column.value"
              class="table-header-total-column"
              :style="{ width: getColWidth(column.value) }"
            >
              <span v-html="formatValue(totalsColumn[column.value])"></span>
            </th>
          </tr>
        </thead>
      </template>
    </mds-data-table>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsDataTable } from '@mds/data-table';
import MdsTooltip from '@mds/tooltip';
import defaultConfig from './_config';

const ignoredTooltipKeys = [
  'portfolio1',
  'portfolio2',
  'name',
  'benchmark1',
  'benchmark2',
  'benchmark',
];

export default {
  name: 'hypo-allocation-table',
  components: {
    MdsDataTable,
    MdsTooltip,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => defaultConfig,
    },
    componentData: {
      type: [String, Object],
      required: true,
    },
  },
  data() {
    return {
      defaultConfig: {
        intlNamespace: this.mwcId,
        ...defaultConfig,
      },
      toggle: false,
      currentTrigger: '',
    };
  },
  computed: {
    componentConfig() {
      return this.getComponentConfig().settings || defaultConfig;
    },
    headers() {
      const colorColumn = {
        fieldName: 'color',
        sortable: false,
        align: 'left',
        transform: value => {
          return `<div class="color-cell" style="background: ${value}" ></div>`;
        },
      };
      const otherColums = this.componentData.columns.map(item => {
        const column = {
          fieldName: item.value,
          sortable: false,
          align: item.value === 'name' ? 'left' : 'right',
          class: 'table-cell',
          style: {
            width: this.getColWidth(item.value),
          },
        };
        if (item.value !== 'name') {
          column.transform = value => this.formatValue(value);
        }
        return column;
      });
      return [colorColumn, ...otherColums];
    },
    rows() {
      let rows = this.componentData.rows.filter(item => {
        return item.name !== 'Total';
      });
      rows = rows.map((item, i) => {
        if (!item.isChild) {
          item.class += ' bold-row';
        }
        return { ...item, id: i };
      });
      return rows;
    },
    totalsColumn() {
      return this.componentData.rows.find(item => item.name === 'Total');
    },
  },
  methods: {
    showName(key) {
      this.currentTrigger = key;
      this.toggle = ignoredTooltipKeys.indexOf(key) < 0;
    },
    hideName() {
      this.toggle = false;
    },
    getColWidth(value) {
      const colNumber = this.componentData.columns.length - 1;
      let colWidth = '50%';
      switch (colNumber) {
        case 2:
          colWidth = '33%';
          break;
        case 3:
          colWidth = '25%';
          break;
        case 4:
          colWidth = value === 'name' ? '30%' : '17.5%';
          break;
        default:
          break;
      }
      return colWidth;
    },
    formatValue(value) {
      return typeof value === 'number'
        ? parseFloat(value).toFixed(this.componentConfig.decimalDigits)
        : value;
    },
  },
};
</script>

<style lang="scss">
@import '~@mds/constants';
@import './styles/variables';

.#{$component-ns} {
  background-color: $mds-background-color-white;
  text-align: center;
  table {
    table-layout: fixed;
    th div {
      display: block;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
  .element {
    background-color: $mds-background-color-white;
    text-align: center;
    display: flex;
  }
  .table-color-column {
    width: 20px;
  }
  .color-cell {
    content: '';
    display: flex;
    width: 10px;
    height: 10px;
    margin-top: 4px;
  }
  .table-header-total-column {
    padding: 0px;
    text-align: right;
    &:nth-child(2) {
      width: 33%;
      text-align: left;
    }
  }
  .table-header-name-column {
    padding: 0px;
    text-align: right;
    line-height: 31px;
  }
  .table-header-names {
    font-size: 14px;
    line-height: 18px;
    color: #5e5e5e;
  }
  .table-header-total {
    color: #1e1e1e;
    background-color: #f2f2f2;
    font-size: 16px;
    line-height: 22px;
  }
  .table-cell {
    padding: 4px 0px;
    font-size: 16px;
  }
  .bold-row {
    td {
      color: #1e1e1e;
      font-weight: bold;
    }
  }
}
</style>
